import { io, Socket } from 'socket.io-client';
import { IRealtimeService } from '../interfaces/IRealtimeService';
import logger from '../loggerService';

/**
 * Web Implementation of Real-time Service using Socket.io
 * Provides enterprise-grade socket management for the web platform.
 */
export class WebRealtimeService implements IRealtimeService {
    private socket: Socket | null = null;
    private url: string;
    private statusListeners: Set<(connected: boolean) => void> = new Set();

    constructor(url: string) {
        this.url = url;
    }

    connect(token: string): void {
        // Prevent redundant connections if already connected
        if (this.socket?.connected) return;

        if (this.socket) {
            this.socket.disconnect();
        }

        this.socket = io(this.url, {
            transports: ['websocket'],
            autoConnect: true,
            forceNew: true,
            auth: {
                token: `Bearer ${token}`,
            },
        });

        this.socket.on('connect', () => {
            logger.info('WebSocket connected (WebRealtimeService)');
            this.notifyStatus(true);
        });

        this.socket.on('disconnect', (reason) => {
            logger.info(`WebSocket disconnected: ${reason} (WebRealtimeService)`);
            this.notifyStatus(false);
        });

        this.socket.on('connect_error', (error) => {
            logger.error('WebSocket connection error', error, 'WebRealtimeService');
            this.notifyStatus(false);
        });
    }

    disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.notifyStatus(false);
        }
    }

    on<T = unknown>(event: string, callback: (data: T) => void): () => void {
        // We cast the callback to standard function type for socket.io internal compatibility
        // but keep the external contract type-safe via the interface.
        // using (...args: unknown[]) => void instead of any for compliance.
        this.socket?.on(event, callback as (...args: unknown[]) => void);
        return () => this.off(event, callback);
    }

    off<T = unknown>(event: string, callback?: (data: T) => void): void {
        if (callback) {
            this.socket?.off(event, callback as (...args: unknown[]) => void);
        } else {
            this.socket?.removeAllListeners(event);
        }
    }

    emit(event: string, ...args: unknown[]): void {
        this.socket?.emit(event, ...args);
    }

    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    subscribeToStatus(callback: (isConnected: boolean) => void): () => void {
        this.statusListeners.add(callback);
        // Immediate call with current status
        callback(this.isConnected());

        return () => {
            this.statusListeners.delete(callback);
        };
    }

    private notifyStatus(connected: boolean): void {
        this.statusListeners.forEach((listener) => listener(connected));
    }
}
